import type { HardeningConfig } from "@keystone/core";

/**
 * Produce a hardened `src/auth.ts` for the generated server. This replaces the
 * default-deny stub Phase-2 emits with a production-grade `jose`-backed JWT verifier.
 *
 * Validation enforced (per RFC 8707, RFC 9068, RFC 9700 / OAuth 2.1 BCP):
 *  1. Token signature verified against AS's JWKS (cached with TTL + auto kid-rotation).
 *  2. `iss` must equal the configured Authorization Server URL.
 *  3. `aud` must include RESOURCE_URI as an exact entry (RFC 8707 audience binding).
 *  4. `exp`/`nbf` checked by `jose.jwtVerify` automatically.
 *  5. `typ` is `at+jwt` (RFC 9068) when set; we tolerate the `JWT` typ for compat.
 *  6. Scopes are extracted from `scope` (space-delimited) and `scopes` (array); both forms supported.
 *  7. No token passthrough — verified tokens are NEVER attached to outbound requests.
 *
 * Error policy: terse, RFC-6750-compliant `WWW-Authenticate` errors. Internal details
 * never leak to the model.
 */
export function renderHardenedAuth(cfg: HardeningConfig & { serverName: string }): string {
  const issuer = cfg.authorizationServerUrl ?? "https://example.invalid";
  const resourceUri = cfg.resourceUri ?? `https://example.invalid/${cfg.serverName}`;
  const jwksUrl =
    cfg.jwksUrl ?? `${issuer.replace(/\/+$/, "")}/.well-known/jwks.json`;
  return `// SPDX-License-Identifier: Apache-2.0
// Hardened by @keystone/hardener — replaces the Phase-2 default-deny stub.
//
// References:
//  - MCP spec rev 2025-11-25 §authorization (current stable, per ADR-0011)
//  - RFC 9068 (JWT profile for OAuth 2.0 Access Tokens)
//  - RFC 8707 (Resource Indicators) — 'aud' audience binding; 'resource' param on
//    BOTH authorize and token requests is REQUIRED on the client side
//  - RFC 9728 (Protected Resource Metadata) — RS-hosted, points at the AS
//  - RFC 9700 (OAuth 2.0 Security BCP)
//
// Client identification precedence per 2025-11-25:
//   1. Pre-registered client (static, AS-issued).
//   2. Client ID Metadata Document (CIMD) — RECOMMENDED. AS metadata advertises
//      'client_id_metadata_document_supported: true'.
//   3. RFC 7591 Dynamic Client Registration — back-compat only; do not require it.
// No token passthrough: validated tokens MUST NOT be forwarded to upstream APIs.
import { createRemoteJWKSet, jwtVerify, errors as joseErrors } from "jose";
import type { OAuthTokenVerifier } from "@modelcontextprotocol/sdk/server/auth/provider.js";
import { InsufficientScopeError, InvalidTokenError } from "@modelcontextprotocol/sdk/server/auth/errors.js";

/** Canonical resource URI used for RFC 8707 audience binding. Pin at boot — DO NOT
 * derive from the request Host header (host-header injection). */
export const RESOURCE_URI = ${JSON.stringify(resourceUri)};

const ISSUER = ${JSON.stringify(issuer)};
const JWKS_URL = ${JSON.stringify(jwksUrl)};

/** alg allowlist: asymmetric only — never HS* (would let token-bearers forge tokens
 * given the JWKS), never 'none'. RS256 / PS256 / ES256 are the modern set. */
const ALLOWED_ALGS = ["RS256", "PS256", "ES256"] as const;

// One long-lived JWKS cache per AS. cacheMaxAge bounds staleness; cooldownDuration
// rate-limits JWKS endpoint hits on kid-miss; timeoutDuration aborts hanging fetches.
const JWKS = createRemoteJWKSet(new URL(JWKS_URL), {
  cacheMaxAge: 10 * 60 * 1000,
  cooldownDuration: 30 * 1000,
  timeoutDuration: 5 * 1000
});

interface JwtClaims {
  sub?: string;
  iss?: string;
  aud?: string | string[];
  exp?: number;
  nbf?: number;
  iat?: number;
  scope?: string;
  scopes?: string[];
  client_id?: string;
  azp?: string;
}

function extractScopes(c: JwtClaims): string[] {
  const out = new Set<string>();
  if (typeof c.scope === "string") for (const s of c.scope.split(/\\s+/)) if (s) out.add(s);
  if (Array.isArray(c.scopes)) for (const s of c.scopes) if (typeof s === "string") out.add(s);
  return Array.from(out);
}

export const verifier: OAuthTokenVerifier = {
  async verifyAccessToken(token: string) {
    if (!token) throw new InvalidTokenError("missing token");
    let payload: JwtClaims;
    try {
      // First try strict RFC 9068 'at+jwt' typ.
      const verified = await jwtVerify(token, JWKS, {
        issuer: ISSUER,
        audience: RESOURCE_URI,
        algorithms: [...ALLOWED_ALGS],
        typ: "at+jwt",
        clockTolerance: "60s",
        requiredClaims: ["sub", "iat", "exp"]
      });
      payload = verified.payload as JwtClaims;
    } catch (e) {
      if (e instanceof joseErrors.JWTExpired) throw new InvalidTokenError("token expired");
      if (e instanceof joseErrors.JOSEAlgNotAllowed) throw new InvalidTokenError("alg not allowed");
      // Some ASs still issue tokens with typ 'JWT'; retry once without typ pin.
      try {
        const verified = await jwtVerify(token, JWKS, {
          issuer: ISSUER,
          audience: RESOURCE_URI,
          algorithms: [...ALLOWED_ALGS],
          clockTolerance: "60s",
          requiredClaims: ["sub", "iat", "exp"]
        });
        payload = verified.payload as JwtClaims;
      } catch (e2) {
        if (e2 instanceof joseErrors.JWTExpired) throw new InvalidTokenError("token expired");
        if (e2 instanceof joseErrors.JOSEAlgNotAllowed) throw new InvalidTokenError("alg not allowed");
        // Generic "invalid" — server-side log carries the detail; the model gets a terse error.
        throw new InvalidTokenError("token verification failed");
      }
    }

    // Defense-in-depth audience check (do not rely on jose's audience match alone:
    // some versions accept any element of an array; we want explicit inclusion).
    const aud = Array.isArray(payload.aud) ? payload.aud : payload.aud ? [payload.aud] : [];
    if (!aud.includes(RESOURCE_URI)) {
      throw new InvalidTokenError("audience does not include this resource");
    }

    return {
      token,
      clientId: payload.client_id ?? payload.azp ?? payload.sub ?? "unknown",
      scopes: extractScopes(payload),
      expiresAt: payload.exp ?? null
    };
  }
};

// Token passthrough guard: re-exported so the host can import without unused warnings,
// and as a documentation hook. Per MCP spec (rev 2025-11-25), validated tokens MUST
// NOT be forwarded to upstream APIs — obtain a separate downstream token.
void InsufficientScopeError;
`;
}
